"use client"

import { datadogRum } from "@datadog/browser-rum"
import { useSignup } from "@nelo/signup-lib"
import React, { useEffect, useRef, useState } from "react"

import { ADDRESS_STEP_TEXTS } from "@modules/signup-and-order-flow/components/signup/constants"
import { StepComponentProps } from "@modules/signup-and-order-flow/components/signup/SignupFlowSteps"
import { MexicanStates as MexicanStatesConst } from "@modules/signup-and-order-flow/mexican-states"

import SelectInput from "../../common/SelectInput"
import StepWrapper from "../../common/StepWrapper"
import TextInput from "../../common/TextInput"

const MexicanStates: string[] = [...MexicanStatesConst]

const AddressStep: React.FC<StepComponentProps> = ({ onComplete, progressPercentage, onBack, treatments, errorHandler, stepId }) => {
  const addressText = ADDRESS_STEP_TEXTS

  const { address } = useSignup()
  const [showSuggestions, setShowSuggestions] = useState(false)
  const suggestionContainerRef = useRef<HTMLDivElement>(null)

  const { street, buildingNumber, interiorNumber, colony, city, state, postalCode, autocompleteSuggestions } = address

  const isFormValid = street.isValid && buildingNumber.isValid && colony.isValid && city.isValid && state.isValid && postalCode.isValid

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionContainerRef.current && !suggestionContainerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    street.setTouched(true)
    street.setBlurred(true)
    buildingNumber.setTouched(true)
    buildingNumber.setBlurred(true)
    interiorNumber.setTouched(true)
    interiorNumber.setBlurred(true)
    colony.setTouched(true)
    colony.setBlurred(true)
    city.setTouched(true)
    city.setBlurred(true)
    state.setTouched(true)
    state.setBlurred(true)
    postalCode.setTouched(true)
    postalCode.setBlurred(true)

    if (!isFormValid) {
      return
    }

    onComplete()
  }

  const formErrorKey = errorHandler.error ? "has-error" : "no-error"

  return (
    <StepWrapper
      key={formErrorKey}
      title={addressText.title}
      subtitle={addressText.subtitle}
      progressPercentage={progressPercentage}
      onSubmit={handleSubmit}
      submitDisabled={!isFormValid || errorHandler.isLoading}
      showBackButton={true}
      onBack={onBack}
      formError={errorHandler.error}
      isLoading={errorHandler.isLoading}
    >
      <div>
        <div className="relative">
          <TextInput
            label={addressText.labels.street}
            value={street.value}
            onChange={(e) => {
              street.setValue(e.target.value)
              setShowSuggestions(true)
            }}
            onBlur={() => {
              street.setTouched(true)
              street.setBlurred(true)
              if (street.errorMessage) {
                datadogRum.addError(new Error(`Street validation error: ${street.errorMessage}`), {
                  step: stepId,
                  field: "street",
                })
              }
              setTimeout(() => {
                if (!suggestionContainerRef.current?.contains(document.activeElement)) {
                  setShowSuggestions(false)
                }
              }, 200)
            }}
            onFocus={() => {
              street.setTouched(true)
              setShowSuggestions(true)
            }}
            isValid={street.isValid}
            touched={street.touched}
            blurred={street.blurred}
            errorMessage={street.errorMessage}
          />
          {showSuggestions && autocompleteSuggestions?.suggestions?.length > 0 && (
            <div
              ref={suggestionContainerRef}
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
            >
              {autocompleteSuggestions.isLoading && (
                <div className="px-4 py-2 text-sm text-gray-500">{addressText.suggestions.loading}</div>
              )}

              {!autocompleteSuggestions.isLoading &&
                autocompleteSuggestions.suggestions.slice(0, 3).map((suggestion) => (
                  <div
                    key={suggestion.placeId}
                    className="cursor-pointer border-b border-gray-100 px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      setShowSuggestions(false)
                      autocompleteSuggestions.handleAddressSuggestionSelect(suggestion)
                    }}
                  >
                    <div className="font-medium">{suggestion.mainText}</div>
                    <div className="text-sm text-gray-500">{suggestion.secondaryText}</div>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <TextInput
            label={addressText.labels.buildingNumber}
            value={buildingNumber.value}
            onChange={(e) => buildingNumber.setValue(e.target.value)}
            onBlur={() => {
              buildingNumber.setTouched(true)
              buildingNumber.setBlurred(true)
              if (buildingNumber.errorMessage) {
                datadogRum.addError(new Error(`Building number validation error: ${buildingNumber.errorMessage}`), {
                  step: stepId,
                  field: "buildingNumber",
                })
              }
            }}
            onFocus={() => buildingNumber.setTouched(true)}
            isValid={buildingNumber.isValid}
            touched={buildingNumber.touched}
            blurred={buildingNumber.blurred}
            errorMessage={buildingNumber.errorMessage}
          />

          <TextInput
            label={addressText.labels.interiorNumber}
            value={interiorNumber.value}
            onChange={(e) => interiorNumber.setValue(e.target.value)}
            onBlur={() => {
              interiorNumber.setTouched(true)
              interiorNumber.setBlurred(true)
              if (interiorNumber.errorMessage) {
                datadogRum.addError(new Error(`Interior number validation error: ${interiorNumber.errorMessage}`), {
                  step: stepId,
                  field: "interiorNumber",
                })
              }
            }}
            onFocus={() => interiorNumber.setTouched(true)}
            isValid={interiorNumber.isValid}
            touched={interiorNumber.touched}
            blurred={interiorNumber.blurred}
            errorMessage={interiorNumber.errorMessage}
          />
        </div>

        <TextInput
          label={addressText.labels.colony}
          value={colony.value}
          onChange={(e) => colony.setValue(e.target.value)}
          onBlur={() => {
            colony.setTouched(true)
            colony.setBlurred(true)
            if (colony.errorMessage) {
              datadogRum.addError(new Error(`Colony validation error: ${colony.errorMessage}`), {
                step: stepId,
                field: "colony",
              })
            }
          }}
          onFocus={() => colony.setTouched(true)}
          isValid={colony.isValid}
          touched={colony.touched}
          blurred={colony.blurred}
          errorMessage={colony.errorMessage}
        />

        <TextInput
          label={addressText.labels.city}
          value={city.value}
          onChange={(e) => city.setValue(e.target.value)}
          onBlur={() => {
            city.setTouched(true)
            city.setBlurred(true)
            if (city.errorMessage) {
              datadogRum.addError(new Error(`City validation error: ${city.errorMessage}`), {
                step: stepId,
                field: "city",
              })
            }
          }}
          onFocus={() => city.setTouched(true)}
          isValid={city.isValid}
          touched={city.touched}
          blurred={city.blurred}
          errorMessage={city.errorMessage}
        />

        <div className="grid grid-cols-2 gap-4">
          <SelectInput
            label={addressText.labels.state}
            options={MexicanStates.map((state) => ({ value: state, label: state }))}
            value={state.value}
            onChange={(e) => state.setValue(e.target.value)}
            onBlur={() => {
              state.setTouched(true)
              state.setBlurred(true)
              if (state.errorMessage) {
                datadogRum.addError(new Error(`State validation error: ${state.errorMessage}`), {
                  step: stepId,
                  field: "state",
                })
              }
            }}
            onFocus={() => state.setTouched(true)}
            isValid={state.isValid}
            touched={state.touched}
            blurred={state.blurred}
            errorMessage={state.errorMessage}
          />

          <TextInput
            label={addressText.labels.postalCode}
            value={postalCode.value}
            onChange={(e) => postalCode.setValue(e.target.value)}
            onBlur={() => {
              postalCode.setTouched(true)
              postalCode.setBlurred(true)
              if (postalCode.errorMessage) {
                datadogRum.addError(new Error(`Postal code validation error: ${postalCode.errorMessage}`), {
                  step: stepId,
                  field: "postalCode",
                })
              }
            }}
            onFocus={() => postalCode.setTouched(true)}
            isValid={postalCode.isValid}
            touched={postalCode.touched}
            blurred={postalCode.blurred}
            errorMessage={postalCode.errorMessage}
            inputMode="numeric"
            maxLength={5}
          />
        </div>
      </div>
    </StepWrapper>
  )
}

export default AddressStep
